import React from 'react';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';

// Importing only the icons you actually use:
import {
  FaReact,
  FaAws,
  FaNodeJs,
  FaBullhorn,
} from 'react-icons/fa';
import {
  SiPython,
  SiMongodb,
  SiPostman,
  SiJira,
} from 'react-icons/si';

// Horizontal scroll animation for mobile
const horizontalMovement = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
`;

// --- your original styled components, unchanged ---
const SkillsSection = styled.section`
  padding: 0.5rem 2rem 2rem;
  min-height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: ${({ theme }) => theme.background};
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const SkillsHeader = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.accent};
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 4px;
    background-color: ${({ theme }) => theme.accent};
    bottom: -10px;
    left: 0;
    animation: underline 1s infinite alternate;
  }

  @keyframes underline {
    from { width: 0; }
    to   { width: 100%; }
  }
`;

// --- card grid container ---
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 1000px;
  margin-top: 2rem;

  @media (max-width: 768px) {
    display: flex;
    animation: ${horizontalMovement} 12s linear infinite;
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.cardBackground};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  text-align: left;
  flex: 0 0 220px;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.8rem;
  color: ${({ color }) => color};
`;

// Floating animation for card titles
const float = keyframes`
  0%,100% { transform: translateY(0); }
  50%    { transform: translateY(-6px); }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.6rem;
  color: ${({ color }) => color};
  animation: ${float} 3s ease-in-out infinite;
`;

const CardList = styled.ul`
  list-style: none;
  padding: 0;
`;

const CardItem = styled.li`
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
  color: ${({ theme }) => theme.color};
`;

// Framer‑Motion variants & hover/tap configs
const skillItemVariants = {
  hidden:   { opacity: 0, scale: 0.8 },
  visible:  { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeInOut' } },
};
const skillIconHover = {
  scale: 1.3, rotateX: 15, rotateY: 15,
  transition: { type: 'spring', stiffness: 300 },
};
const skillIconClick = {
  scale: 0.95, transition: { type: 'spring', stiffness: 300 },
};

const Skills = () => {
  const categories = [
    {
      title: 'Frontend',
      icon: <FaReact />,
      color: '#61DBFB',
      items: ['ReactJS', 'JavaScript', 'TypeScript', 'Tailwind CSS'],
    },
    {
      title: 'Backend',
      icon: <FaNodeJs />,
      color: '#3C873A',
      items: ['Node.js', 'ExpressJS', 'NestJS'],
    },
    {
      title: 'Databases',
      icon: <SiMongodb />,
      color: '#47A248',
      items: ['MongoDB', 'MySQL', 'PostgreSQL'],
    },
    {
      title: 'Cloud & DevOps',
      icon: <FaAws />,
      color: '#FF9900',
      items: ['AWS', 'Docker', 'GitHub'],
    },
    {
      title: 'Languages',
      icon: <SiPython />,
      color: '#306998',
      items: ['Python', 'C++', 'JavaScript', 'Java'],
    },
    {
      title: 'Testing',
      icon: <SiPostman />,
      color: '#FF6C37',
      items: ['Postman', 'API Testing'],
    },
    {
      title: 'Project Management',
      icon: <SiJira />,
      color: '#0052CC',
      items: ['Agile & Scrum', 'JIRA', 'Trello', 'Confluence'],
    },
    {
      title: 'SEO & Marketing',
      icon: <FaBullhorn />,
      color: '#FF6F61',
      items: ['SEO Optimization', 'Keyword Research', 'Google Analytics', 'PPC Campaigns'],
    },
  ];

  return (
    <SkillsSection id="skills">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <SkillsHeader>Skills</SkillsHeader>
        <CardGrid>
          {categories.map((cat, i) => (
            <Card
              key={i}
              variants={skillItemVariants}
              initial="hidden"
              animate="visible"
            >
              <CardIcon color={cat.color}>
                <motion.div
                  whileHover={skillIconHover}
                  whileTap={skillIconClick}
                >
                  {cat.icon}
                </motion.div>
              </CardIcon>
              <CardTitle color={cat.color}>{cat.title}</CardTitle>
              <CardList>
                {cat.items.map((skill, j) => (
                  <CardItem key={j}>• {skill}</CardItem>
                ))}
              </CardList>
            </Card>
          ))}
        </CardGrid>
      </motion.div>
    </SkillsSection>
  );
};

export default Skills;
